import React, { lazy, Suspense } from 'react';
import { render, RenderResult } from '@testing-library/react';
import { Navigate, useRoutes, MemoryRouter } from 'react-router-dom';

import { DefaultLayout } from 'src/layouts';
import { useUserStore } from 'src/stores/user/user.store';
import { DEFAULT_EVENT, TEST_AB_EVENT_ID } from 'src/config-global';

import { SplashScreen } from 'src/components/loading-screen';

import { paths } from '../paths';
import { errorRoutes } from './error';
import { AuthGuard } from '../../auth/guard';
import { useCustomContent } from '../../stores/custom-content/custom-content.store';

// PLOP.JS COMMENT BELOW FOR SCRIPTS, PLEASE DON'T REMOVE
// PAGE_IMPORTS
const Parking = lazy(() => import('../../modules/parking/parking/parking'));
const ParkingReceipt = lazy(() => import('../../modules/parkingReceipt/parking-receipt'));
const Wifi = lazy(() => import('../../modules/wifi/wifi/wifi'));
const Login = lazy(() => import('../../modules/auth/login/login'));
const LoginB = lazy(() => import('../../modules/auth_B/login/login'));
const LoginPreview = lazy(() => import('../../modules/auth/login-preview/login-preview'));
const RegisterPreview = lazy(() => import('../../modules/auth/register-preview/register-preview'));
const Home = lazy(() => import('../../modules/home/home'));
const HomeB = lazy(() => import('../../modules/home_B/home'));
const Benefits = lazy(() => import('../../modules/benefits/benefits'));
const SatisfactionSurvey = lazy(() => import('../../modules/benefits/satisfaction-survey'));
const Profile = lazy(() => import('../../modules/user/profile/profile'));

export const renderWithRouter = (ui: React.ReactElement): RenderResult =>
  render(<MemoryRouter>{ui}</MemoryRouter>);

export default function Router() {
  const { pathname } = window.location;
  const segments = pathname.split('/');
  const { eventId } = useCustomContent((state) => state.ids);
  const { rdSummitVersion } = useUserStore();

  const bu = segments[1];
  const event = segments[2];
  return useRoutes([
    {
      path: paths.root,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <AuthGuard>
            <Navigate to={`/${bu}/${event}/home`} replace />
          </AuthGuard>
        </Suspense>
      ),
    },

    { path: '*', element: <Navigate to={paths.error.notFound} replace /> },

    ...errorRoutes,

    // PLOP.JS COMMENT FOR BELOW SCRIPTS, PLEASE DON'T REMOVE
    // RELATION_PATH_PAGES
    { path: paths.wifi.root, element: <Wifi /> },
    {
      path: paths.parkingReceipt.root(':id'),
      element: (
        <AuthGuard>
          <DefaultLayout dontHaveFooter>
            <ParkingReceipt />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.parking.root(':id'),
      element: (
        <AuthGuard>
          <DefaultLayout dontHaveFooter>
            <Parking />
          </DefaultLayout>
        </AuthGuard>
      ),
    },
    {
      path: paths.auth.login,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout isLogin dontHaveFooter>
            {eventId === TEST_AB_EVENT_ID || eventId === DEFAULT_EVENT ? <LoginB /> : <Login />}
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      // Preview isolado: este bloco pode ser comentado sem alterar o login atual.
      path: paths.auth.loginPreview,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout isLogin dontHaveFooter>
            <LoginPreview />
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      // Cadastro de preview: comente este bloco para remover apenas a demonstração.
      path: paths.auth.registerPreview,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <DefaultLayout isLogin dontHaveFooter>
            <RegisterPreview />
          </DefaultLayout>
        </Suspense>
      ),
    },
    {
      path: paths.home.root,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <AuthGuard>
            <DefaultLayout>
              {(eventId === TEST_AB_EVENT_ID && rdSummitVersion === 'B') ||
              eventId === DEFAULT_EVENT ? (
                <HomeB />
              ) : (
                <Home />
              )}
            </DefaultLayout>
          </AuthGuard>
        </Suspense>
      ),
    },
    {
      path: paths.user.profile,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <AuthGuard>
            <DefaultLayout>
              <Profile />
            </DefaultLayout>
          </AuthGuard>
        </Suspense>
      ),
    },
    {
      path: paths.benefits.root,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <AuthGuard>
            <DefaultLayout>
              <Benefits />
            </DefaultLayout>
          </AuthGuard>
        </Suspense>
      ),
    },
    {
      path: paths.benefits.satisfactionSurvey,
      element: (
        <Suspense fallback={<SplashScreen />}>
          <AuthGuard>
            <DefaultLayout>
              <SatisfactionSurvey />
            </DefaultLayout>
          </AuthGuard>
        </Suspense>
      ),
    },
  ]);
}
